"use client";

import type {} from "../osano";
import {
	applyGpc,
	parseTrackingConsentCookie,
	serializeTrackingConsent,
	TRACKING_CONSENT_COOKIE,
	TRACKING_CONSENT_TTL_SECONDS,
	type TrackingConsent,
} from "./consent";

type ConsentSubscriber = (consent: TrackingConsent) => void;

const subscribers = new Set<ConsentSubscriber>();
let initialized = false;

function readCookie(name: string): string | undefined {
	const match = document.cookie
		.split("; ")
		.find((row) => row.startsWith(`${name}=`));
	return match?.slice(name.length + 1);
}

function hasGlobalPrivacyControl(): boolean {
	return navigator.globalPrivacyControl === true;
}

function readCurrentConsent(): TrackingConsent {
	return applyGpc(
		parseTrackingConsentCookie(readCookie(TRACKING_CONSENT_COOKIE)),
		hasGlobalPrivacyControl()
	);
}

function parentDomainAttribute(): string {
	return window.location.hostname.endsWith("artofproblemsolving.com")
		? "; domain=.artofproblemsolving.com"
		: "";
}

function writeConsentCookie(consent: TrackingConsent): void {
	document.cookie = `${TRACKING_CONSENT_COOKIE}=${serializeTrackingConsent(consent)}; max-age=${TRACKING_CONSENT_TTL_SECONDS}; path=/; secure; samesite=lax${parentDomainAttribute()}`;
}

function updateConsent(consent: TrackingConsent): void {
	for (const subscriber of subscribers) {
		subscriber(consent);
	}
}

function consentFromOsano(cm: OsanoConsentManager): TrackingConsent {
	const osanoConsent = cm.getConsent();
	return applyGpc(
		{
			analytics: osanoConsent.ANALYTICS !== "DENY",
			marketing:
				osanoConsent.MARKETING !== "DENY" &&
				osanoConsent.OPT_OUT !== "ACCEPT" &&
				osanoConsent["OPT-OUT"] !== "ACCEPT",
		},
		hasGlobalPrivacyControl()
	);
}

function syncFromOsano(
	cm: OsanoConsentManager,
	hasExplicitChoice: boolean
): void {
	if (cm.mode === "debug") {
		return;
	}
	if (cm.consentModel !== "implicit" && !hasExplicitChoice) {
		return;
	}

	const consent = consentFromOsano(cm);
	writeConsentCookie(consent);
	updateConsent(consent);
}

export function initConsentBridge(): void {
	if (initialized || typeof window === "undefined") {
		return;
	}

	const cm = window.Osano?.cm;
	// Osano may not be on the page yet. Leave the bridge uninitialized so a
	// later read can attach listeners. A missing CMP fails open.
	if (!cm) {
		return;
	}
	initialized = true;
	if (cm.mode === "debug") {
		return;
	}

	cm.addEventListener("osano-cm-initialized", (consent) => {
		syncFromOsano(cm, consent !== undefined);
	});
	cm.addEventListener("osano-cm-consent-saved", () => {
		syncFromOsano(cm, true);
	});
	// Osano is a blocking head script, so osano-cm-initialized has usually
	// already fired. Implicit consent is fully represented by getConsent().
	// Explicit consent still waits for a saved choice: an unsaved default is
	// not an opt-out.
	syncFromOsano(cm, false);
}

/** Browser-only. Server senders log on their own; this covers skips that never leave the page. */
export function logConsentSkip(event: string): void {
	if (process.env.NODE_ENV === "production") {
		return;
	}
	console.log(`[consent] skipped ${event} (analytics denied)`);
}

export function getTrackingConsentSync(): TrackingConsent {
	if (typeof window === "undefined") {
		return {analytics: true, marketing: true};
	}
	if (!initialized) {
		initConsentBridge();
	}
	return readCurrentConsent();
}

export function onTrackingConsentChange(
	subscriber: ConsentSubscriber
): () => void {
	subscribers.add(subscriber);
	return () => subscribers.delete(subscriber);
}
